import { useState, useEffect } from 'react'
import { useCurrentUser, cacheCurrentUser } from '../hooks/useCurrentUser'
import userService from '@/features/onboarding/services/userService'
import { fbTrackViewContent } from '@/shared/utils/facebookPixel'
import type {
  StudentClass,
  StudyType,
  Stream,
  UpdateProfileMetadataRequest,
  NeetStudentProfileMetadata,
} from '@/features/onboarding/types'
import './ProfilePage.css'

// Indian states and union territories list (alphabetically sorted)
const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chandigarh',
  'Chhattisgarh', 'Delhi', 'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh',
  'Jammu and Kashmir', 'Jharkhand', 'Karnataka', 'Kerala', 'Ladakh',
  'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Puducherry', 'Punjab', 'Rajasthan', 'Sikkim',
  'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand',
  'West Bengal',
]

const STUDENT_CLASS_OPTIONS: { value: StudentClass; label: string }[] = [
  { value: 'CLASS_11', label: 'Class 11' },
  { value: 'CLASS_12', label: 'Class 12' },
  { value: 'DROPPER', label: 'Dropper (Gap Year)' },
  { value: 'REPEATER', label: 'Repeater' },
]

const STUDY_TYPE_OPTIONS: { value: StudyType; label: string }[] = [
  { value: 'SCHOOL_ONLY', label: 'School Only' },
  { value: 'ONLINE_COACHING', label: 'Online Coaching' },
  { value: 'OFFLINE_COACHING', label: 'Offline Coaching' },
  { value: 'SELF_STUDY', label: 'Self Study' },
]

const STREAM_OPTIONS: { value: Stream; label: string }[] = [
  { value: 'PCB', label: 'PCB (Physics, Chemistry, Biology)' },
  { value: 'PCMB', label: 'PCMB (Physics, Chemistry, Maths, Biology)' },
]

// Generate year options (current year to +5 years)
const currentYear = new Date().getFullYear()
const YEAR_OPTIONS = Array.from({ length: 6 }, (_, i) => currentYear + i)

interface ProfileFormData {
  firstName: string
  lastName: string
  studentClass: StudentClass | ''
  stream: Stream | ''
  state: string
  city: string
  targetNeetYear: number | ''
  targetNeetScore: number | ''
  studyType: StudyType | ''
  previousNeetScore: number | ''
  averageStudyHoursPerDay: number | ''
  parentName: string
  parentMobileNumber: string
}

interface FormErrors {
  targetNeetScore?: string
  previousNeetScore?: string
  averageStudyHoursPerDay?: string
}

const ProfilePage = () => {
  const { user: userProfile } = useCurrentUser()
  const [isEditing, setIsEditing] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [errors, setErrors] = useState<FormErrors>({})

  const [formData, setFormData] = useState<ProfileFormData>({
    firstName: '',
    lastName: '',
    studentClass: '',
    stream: '',
    state: '',
    city: '',
    targetNeetYear: '',
    targetNeetScore: '',
    studyType: '',
    previousNeetScore: '',
    averageStudyHoursPerDay: '',
    parentName: '',
    parentMobileNumber: '',
  })

  // Track ViewContent for FB Pixel
  useEffect(() => {
    fbTrackViewContent('profile', undefined, 'My Profile')
  }, [])

  // Load user profile data (don't clobber in-progress edits on background refetch)
  useEffect(() => {
    if (userProfile && !isEditing) {
      setFormData({
        firstName: userProfile.firstName || '',
        lastName: userProfile.lastName || '',
        studentClass: userProfile.profileMetadata?.studentClass || '',
        stream: userProfile.profileMetadata?.stream || '',
        state: userProfile.profileMetadata?.state || '',
        city: userProfile.profileMetadata?.city || '',
        targetNeetYear: userProfile.profileMetadata?.targetNeetYear || '',
        targetNeetScore: userProfile.profileMetadata?.targetNeetScore || '',
        studyType: userProfile.profileMetadata?.studyType || '',
        previousNeetScore: userProfile.profileMetadata?.previousNeetScore || '',
        averageStudyHoursPerDay: userProfile.profileMetadata?.averageStudyHoursPerDay || '',
        parentName: userProfile.profileMetadata?.parentName || '',
        parentMobileNumber: userProfile.profileMetadata?.parentMobileNumber || '',
      })
    }
  }, [userProfile, isEditing])

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }))
    }
    // Clear success message when editing
    setSuccess(null)
  }

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}

    if (formData.targetNeetScore) {
      const score = Number(formData.targetNeetScore)
      if (isNaN(score) || score < 0 || score > 720) {
        newErrors.targetNeetScore = 'Score must be between 0 and 720'
      }
    }

    if (formData.previousNeetScore) {
      const score = Number(formData.previousNeetScore)
      if (isNaN(score) || score < 0 || score > 720) {
        newErrors.previousNeetScore = 'Score must be between 0 and 720'
      }
    }

    if (formData.averageStudyHoursPerDay) {
      const hours = Number(formData.averageStudyHoursPerDay)
      if (isNaN(hours) || hours < 0 || hours > 24) {
        newErrors.averageStudyHoursPerDay = 'Hours must be between 0 and 24'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleCancel = () => {
    // Reset form to original values
    if (userProfile) {
      setFormData({
        firstName: userProfile.firstName || '',
        lastName: userProfile.lastName || '',
        studentClass: userProfile.profileMetadata?.studentClass || '',
        stream: userProfile.profileMetadata?.stream || '',
        state: userProfile.profileMetadata?.state || '',
        city: userProfile.profileMetadata?.city || '',
        targetNeetYear: userProfile.profileMetadata?.targetNeetYear || '',
        targetNeetScore: userProfile.profileMetadata?.targetNeetScore || '',
        studyType: userProfile.profileMetadata?.studyType || '',
        previousNeetScore: userProfile.profileMetadata?.previousNeetScore || '',
        averageStudyHoursPerDay: userProfile.profileMetadata?.averageStudyHoursPerDay || '',
        parentName: userProfile.profileMetadata?.parentName || '',
        parentMobileNumber: userProfile.profileMetadata?.parentMobileNumber || '',
      })
    }
    setErrors({})
    setError(null)
    setIsEditing(false)
  }

  const handleSave = async () => {
    if (!validateForm()) return
    if (!userProfile?.userId) {
      setError('User ID not found')
      return
    }

    setIsSubmitting(true)
    setError(null)
    setSuccess(null)

    try {
      const profileMetadata: NeetStudentProfileMetadata = {
        profileType: 'NEET_STUDENT',
        ...(formData.studentClass && { studentClass: formData.studentClass as StudentClass }),
        ...(formData.state && { state: formData.state }),
        ...(formData.stream && { stream: formData.stream as Stream }),
        ...(formData.city && { city: formData.city }),
        ...(formData.targetNeetYear && { targetNeetYear: Number(formData.targetNeetYear) }),
        ...(formData.targetNeetScore && { targetNeetScore: Number(formData.targetNeetScore) }),
        ...(formData.studyType && { studyType: formData.studyType as StudyType }),
        ...(formData.previousNeetScore && { previousNeetScore: Number(formData.previousNeetScore) }),
        ...(formData.averageStudyHoursPerDay && { averageStudyHoursPerDay: Number(formData.averageStudyHoursPerDay) }),
        ...(formData.parentName && { parentName: formData.parentName }),
        ...(formData.parentMobileNumber && { parentMobileNumber: formData.parentMobileNumber }),
      }

      const request: UpdateProfileMetadataRequest = {
        profileMetadata,
      }

      const response = await userService.updateProfileMetadata(userProfile.userId, request)

      if (response.success) {
        // Update the cached profile so all consumers see the change
        cacheCurrentUser(response.data)
        setSuccess('Profile updated successfully!')
        setIsEditing(false)
      } else {
        throw new Error(response.message || 'Failed to update profile')
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update profile'
      setError(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const getClassLabel = (value: string) => {
    return STUDENT_CLASS_OPTIONS.find((o) => o.value === value)?.label || '-'
  }

  const getStreamLabel = (value: string) => {
    return STREAM_OPTIONS.find((o) => o.value === value)?.label || '-'
  }

  const getStudyTypeLabel = (value: string) => {
    return STUDY_TYPE_OPTIONS.find((o) => o.value === value)?.label || '-'
  }

  return (
    <div className="profile-page">
      <div className="profile-header">
        <h1>My Profile</h1>
        {!isEditing && (
          <button
            type="button"
            className="btn-edit"
            onClick={() => setIsEditing(true)}
          >
            Edit Profile
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="profile-content">
        {/* Basic Information */}
        <div className="profile-section">
          <h2>Basic Information</h2>
          <div className="profile-grid">
            <div className="profile-field">
              <label>First Name</label>
              {isEditing ? (
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  placeholder="Enter first name"
                  disabled
                />
              ) : (
                <span className="field-value">{formData.firstName || '-'}</span>
              )}
              {isEditing && <span className="field-hint">Name cannot be changed here</span>}
            </div>

            <div className="profile-field">
              <label>Last Name</label>
              {isEditing ? (
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder="Enter last name"
                  disabled
                />
              ) : (
                <span className="field-value">{formData.lastName || '-'}</span>
              )}
            </div>

            <div className="profile-field">
              <label>Email</label>
              <span className="field-value">{userProfile?.email || '-'}</span>
            </div>
          </div>
        </div>

        {/* Academic Information */}
        <div className="profile-section">
          <h2>Academic Information</h2>
          <div className="profile-grid">
            <div className="profile-field">
              <label>Current Class</label>
              {isEditing ? (
                <select
                  name="studentClass"
                  value={formData.studentClass}
                  onChange={handleInputChange}
                >
                  <option value="">Select class</option>
                  {STUDENT_CLASS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="field-value">{getClassLabel(formData.studentClass)}</span>
              )}
            </div>

            <div className="profile-field">
              <label>Stream</label>
              {isEditing ? (
                <select
                  name="stream"
                  value={formData.stream}
                  onChange={handleInputChange}
                >
                  <option value="">Select stream</option>
                  {STREAM_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="field-value">{getStreamLabel(formData.stream)}</span>
              )}
            </div>

            <div className="profile-field">
              <label>State</label>
              {isEditing ? (
                <select
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                >
                  <option value="">Select state</option>
                  {INDIAN_STATES.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="field-value">{formData.state || '-'}</span>
              )}
            </div>

            <div className="profile-field">
              <label>City</label>
              {isEditing ? (
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="Enter city"
                />
              ) : (
                <span className="field-value">{formData.city || '-'}</span>
              )}
            </div>
          </div>
        </div>

        {/* NEET Goals */}
        <div className="profile-section">
          <h2>NEET Goals</h2>
          <div className="profile-grid">
            <div className="profile-field">
              <label>Target NEET Year</label>
              {isEditing ? (
                <select
                  name="targetNeetYear"
                  value={formData.targetNeetYear}
                  onChange={handleInputChange}
                >
                  <option value="">Select year</option>
                  {YEAR_OPTIONS.map((year) => (
                    <option key={year} value={year}>
                      NEET {year}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="field-value">
                  {formData.targetNeetYear ? `NEET ${formData.targetNeetYear}` : '-'}
                </span>
              )}
            </div>

            <div className="profile-field">
              <label>Target Score (out of 720)</label>
              {isEditing ? (
                <>
                  <input
                    type="number"
                    name="targetNeetScore"
                    value={formData.targetNeetScore}
                    onChange={handleInputChange}
                    placeholder="e.g., 650"
                    min="0"
                    max="720"
                  />
                  {errors.targetNeetScore && (
                    <span className="field-error">{errors.targetNeetScore}</span>
                  )}
                </>
              ) : (
                <span className="field-value">
                  {formData.targetNeetScore ? `${formData.targetNeetScore}/720` : '-'}
                </span>
              )}
            </div>

            <div className="profile-field">
              <label>Study Type</label>
              {isEditing ? (
                <select
                  name="studyType"
                  value={formData.studyType}
                  onChange={handleInputChange}
                >
                  <option value="">Select study type</option>
                  {STUDY_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="field-value">{getStudyTypeLabel(formData.studyType)}</span>
              )}
            </div>

            <div className="profile-field">
              <label>Average Study Hours/Day</label>
              {isEditing ? (
                <>
                  <input
                    type="number"
                    name="averageStudyHoursPerDay"
                    value={formData.averageStudyHoursPerDay}
                    onChange={handleInputChange}
                    placeholder="e.g., 6"
                    min="0"
                    max="24"
                  />
                  {errors.averageStudyHoursPerDay && (
                    <span className="field-error">{errors.averageStudyHoursPerDay}</span>
                  )}
                </>
              ) : (
                <span className="field-value">
                  {formData.averageStudyHoursPerDay ? `${formData.averageStudyHoursPerDay} hours` : '-'}
                </span>
              )}
            </div>

            <div className="profile-field">
              <label>Previous NEET Score</label>
              {isEditing ? (
                <>
                  <input
                    type="number"
                    name="previousNeetScore"
                    value={formData.previousNeetScore}
                    onChange={handleInputChange}
                    placeholder="If attempted before"
                    min="0"
                    max="720"
                  />
                  {errors.previousNeetScore && (
                    <span className="field-error">{errors.previousNeetScore}</span>
                  )}
                </>
              ) : (
                <span className="field-value">
                  {formData.previousNeetScore ? `${formData.previousNeetScore}/720` : '-'}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Parent/Guardian Information */}
        <div className="profile-section">
          <h2>Parent/Guardian Information</h2>
          <div className="profile-grid">
            <div className="profile-field">
              <label>Parent Name</label>
              {isEditing ? (
                <input
                  type="text"
                  name="parentName"
                  value={formData.parentName}
                  onChange={handleInputChange}
                  placeholder="Enter parent's name"
                />
              ) : (
                <span className="field-value">{formData.parentName || '-'}</span>
              )}
            </div>

            <div className="profile-field">
              <label>Parent Mobile</label>
              {isEditing ? (
                <input
                  type="tel"
                  name="parentMobileNumber"
                  value={formData.parentMobileNumber}
                  onChange={handleInputChange}
                  placeholder="+919876543210"
                />
              ) : (
                <span className="field-value">{formData.parentMobileNumber || '-'}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {isEditing && (
        <div className="profile-actions">
          <button
            type="button"
            className="btn-cancel"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-save"
            onClick={handleSave}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}
    </div>
  )
}

export default ProfilePage
